import { Field, useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { hasCookieConsent, isAbTestActive } from '../../../utils/abTests';
import camelCase from '../../../utils/camelCase';
import {
  LOADING_TYPES,
  Notification,
  PaymentOption,
  RecurringPaymentOption,
  useCheckoutState
} from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import useDeepCompare from '../../../utils/useDeepCompare';
import FormattedCurrency from '../../atoms/FormattedCurrency';
import Paragraph from '../../atoms/Paragraph';
import SubHeadline from '../../atoms/SubHeadline';
import OptionsField from '../../formik/OptionsField';
import DynamicFieldArea from '../DynamicFieldArea';
import { showDefaultError, showNotification } from '../Notifications';
import additionalPaymentConfig from './additionalPaymentConfig';
import './style.scss';

const RECURRING_OPTIONS_WITH_WIDGETS = [
  'adyen_creditcard',
  'adyen_creditcard_bc',
  'spay_adyen_credit_card',
  'spay_adyen_creditcard_bc'
];

const PaymentOptions = () => {
  const { checkoutState, setPaymentOption, isLoading, setLoading } = useCheckoutState();
  const { formatMessage } = useIntl();
  const { values, setFieldValue } = useFormikContext<{
    paymentMethod: string;
    carrier: { carrierKey: string; carrierGroupKey: string; shippingOptionKey: string };
  }>();
  const { orderOptions, orderState, customer } = checkoutState;
  const hasCodPaymentFees = customer!.flags?.hasCodPaymentFees;
  const currentPaymentOptions = useDeepCompare(orderOptions?.payment);
  const currentPaymentMethod = useDeepCompare(orderState?.payment?.selected?.[0]);

  useEffect(() => {
    const paymentMethod =
      orderState.payment?.selected?.[0].recurringPaymentOptionId ??
      orderState.payment?.selected?.[0].paymentOptionKey ??
      orderState.payment?.giftCards?.[0].paymentOptionKey ??
      '';

    if (paymentMethod !== values.paymentMethod) {
      setFieldValue('paymentMethod', paymentMethod);
    }
  }, [currentPaymentOptions, currentPaymentMethod]);

  const isPaymentDisabled = !orderState.addresses?.billing;

  const isShortInfoEnabled = config.paymentOptions.descriptions.short;
  const isToolTipEnabled = config.paymentOptions.descriptions.toolTip;

  const getShortDescription = ({
    label,
    isDisabled,
    isRecurring,
    i18nKey,
    notifications
  }: {
    label: string;
    isDisabled: boolean;
    i18nKey: string;
    isRecurring?: boolean;
    notifications?: Notification[];
  }) => {
    if (notifications?.length) {
      return notifications.flatMap(({ text, textTranslationKey }, i) => [
        text ?? formatMessage({ id: textTranslationKey }),
        i !== notifications.length - 1 && ' '
      ]);
    } else if (!isDisabled && !isRecurring && isShortInfoEnabled) {
      return formatMessage({ id: `payment.paymentMethod.optionDescriptions.${i18nKey}.shortDescription` });
    } else if (isDisabled && !isPaymentDisabled) {
      return formatMessage({ id: 'payment.paymentMethod.disabled' }, { paymentMethod: label });
    } else {
      return undefined;
    }
  };

  const getOptionCreator = (isRecurring?: boolean) => (option: PaymentOption & Partial<RecurringPaymentOption>) => {
    const {
      i18nKey,
      useI18nKeyAsIconKey,
      iconKeys,
      countryIconKeys,
      localeIconKeys,
      form: Form
    } = additionalPaymentConfig[option.paymentOptionKey] ?? {
      i18nKey: camelCase(option.paymentOptionKey),
      useI18nKeyAsIconKey: true
    };

    const label = formatMessage({ id: `payment.paymentMethod.options.${i18nKey}` });

    const getDefaultIcon = () => {
      if (config.paymentOptions.hidePaymentIcons) {
        return undefined;
      }
      const rawCreditcardBrand = option.recurringPaymentInformation?.creditcardBrand;
      if (rawCreditcardBrand) {
        const creditcardBrand =
          ['mc', 'MASTER'].includes(rawCreditcardBrand) ? 'mastercard'
          : rawCreditcardBrand === 'bcmc' ? 'bancontact'
          : rawCreditcardBrand.toLocaleLowerCase();
        return getAssetUrl(`payment/${creditcardBrand}.svg`);
      } else if (useI18nKeyAsIconKey) {
        return getAssetUrl(`payment/${i18nKey}.svg`);
      } else {
        return undefined;
      }
    };

    const getLabel = () => {
      if (isRecurring) {
        if (option.recurringPaymentInformation?.maskedCreditcardNumber) {
          return `•••• ${option.recurringPaymentInformation.maskedCreditcardNumber.replace(/X/g, '')}`;
        } else if (option.recurringPaymentInformation?.customerEmail) {
          return option.recurringPaymentInformation.customerEmail;
        }
      }
      return label;
    };

    const getAdditionalFeeInfo = (feeKey: string) =>
      option.configuration?.[feeKey] && {
        additionalInfo: formatMessage(
          { id: 'payment.paymentMethod.additionalFee' },
          { fee: <FormattedCurrency value={option.configuration[feeKey]} /> }
        )
      };

    const shouldShowToolTip = !isRecurring && isToolTipEnabled;

    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowPaymentOptionKey = urlParams.get('showPaymentOptionKeys') === 'true';

    const paymentLogoOverwrites = config.paymentOptions.paymentBrandsList?.find(
      paymentOption => paymentOption.paymentOptionKey === option.paymentOptionKey
    )?.icons;

    const firstFourLogos = paymentLogoOverwrites?.slice(0, 4);
    const restOfLogos = paymentLogoOverwrites?.slice(4);

    return {
      key: isRecurring ? option.recurringPaymentOptionId : option.paymentOptionKey,
      defaultIcon: getDefaultIcon(),
      label: getLabel(),
      defaultIcons:
        !config.paymentOptions.hidePaymentIcons &&
        (firstFourLogos ?? countryIconKeys?.[config.country] ?? localeIconKeys?.[config.locale] ?? iconKeys)?.map(i =>
          getAssetUrl(`payment/${i}.svg`)
        ),
      hiddenIcons: !config.paymentOptions.hidePaymentIcons && restOfLogos?.map(i => getAssetUrl(`payment/${i}.svg`)),
      isDisabled: isPaymentDisabled || option.isDisabled,
      shortDescription: getShortDescription({
        label,
        isDisabled: option.isDisabled,
        i18nKey: i18nKey!,
        isRecurring,
        notifications: option.notifications
      }),
      ...getAdditionalFeeInfo(option.configuration?.codPaymentFee && hasCodPaymentFees ? 'codPaymentFee' : 'fee'),
      ...(shouldShowToolTip && {
        toolTip: {
          headline: formatMessage({ id: `payment.paymentMethod.optionDescriptions.${i18nKey}.toolTipHeadline` }),
          content: formatMessage({ id: `payment.paymentMethod.optionDescriptions.${i18nKey}.toolTipText` })
        }
      }),
      ...(isRecurring && { paymentOptionKey: option.paymentOptionKey }),
      ...((!isRecurring || RECURRING_OPTIONS_WITH_WIDGETS.includes(option.paymentOptionKey)) && {
        form: (
          <>
            {Form && <Form isSPAY={option.paymentOptionKey.includes('spay')} isRecurring={isRecurring} />}
            {shouldShowPaymentOptionKey && option.paymentOptionKey}
            <DynamicFieldArea area="paymentOptions" conditionValues={{ paymentOptionKey: option.paymentOptionKey }} />
          </>
        )
      })
    };
  };

  const genericOptions = useMemo(
    () =>
      currentPaymentOptions?.generic
        .filter(
          paymentOption =>
            ((hasCookieConsent() || config.country !== 'DE' || config.shop !== 'ay') &&
              !isAbTestActive('ratepayHidden')) ||
            paymentOption.paymentOptionKey !== 'ratepay_invoice'
        )

        .map(getOptionCreator()) ?? [],
    [currentPaymentOptions, isPaymentDisabled, isAbTestActive('ratepayHidden')]
  );

  const recurringOptions = useMemo(
    () =>
      currentPaymentOptions?.recurring
        ?.filter(({ paymentOptionKey }) => !paymentOptionKey.includes('cod'))
        .map(getOptionCreator(true)),
    [currentPaymentOptions, isPaymentDisabled]
  );

  const onChange = async (paymentOptionKey: string) => {
    setLoading(LOADING_TYPES.paymentChange(paymentOptionKey));
    const previousValue = values.paymentMethod;
    try {
      await setPaymentOption({ paymentOptionKey });
    } catch (e) {
      setFieldValue('paymentMethod', previousValue);
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'set_payment_option');
    }
    setLoading(LOADING_TYPES.off);
  };

  const onChangeRecurring = async (recurringPaymentOptionId: string) => {
    setLoading(LOADING_TYPES.paymentChange(recurringPaymentOptionId));

    const previousValue = values.paymentMethod;
    const recurringOption = recurringOptions?.find(
      _recurringOption => recurringPaymentOptionId === _recurringOption.key
    );
    try {
      await setPaymentOption({
        paymentOptionKey: recurringOption!.paymentOptionKey!,
        recurringPaymentOptionId
      });
    } catch (e) {
      setFieldValue('paymentMethod', previousValue);
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'set_payment_option');
    }
    setLoading(LOADING_TYPES.off);
  };

  useEffect(() => {
    if (!isPaymentDisabled && !genericOptions.length) {
      showNotification({
        level: 'info',
        text: formatMessage({ id: 'payment.paymentMethod.notifications.noPaymentMethods' }),
        autoClose: true
      });
    }

    if (!isPaymentDisabled && genericOptions.some(genericOption => genericOption.isDisabled)) {
      showNotification({
        level: 'info',
        text: formatMessage({ id: 'payment.paymentMethod.notifications.notAvailable' }),
        autoClose: true
      });
    }
  }, []);

  return (
    <div className="payment-options">
      <SubHeadline>
        <FormattedMessage id="payment.paymentMethod.headline" />
      </SubHeadline>
      {isPaymentDisabled && (
        <Paragraph>
          <FormattedMessage id="payment.paymentMethod.noBillingAddress" />
        </Paragraph>
      )}
      {!!recurringOptions?.length && (
        <>
          <Paragraph>
            <FormattedMessage id="payment.subheadlineRecurring" />
          </Paragraph>
          <Field
            name="paymentMethod"
            component={OptionsField}
            options={recurringOptions}
            isVertical
            onChange={onChangeRecurring}
            isLoading={isLoading.replace('payment:change-', '')}
            dataTestIdPrefix="recurringPaymentOptions"
          />
          <Paragraph>
            <FormattedMessage id="payment.subheadlineOther" />
          </Paragraph>
        </>
      )}
      <Field
        name="paymentMethod"
        component={OptionsField}
        options={genericOptions}
        isVertical
        dataTestIdPrefix="paymentOptions"
        onChange={onChange}
        isLoading={isLoading.replace('payment:change-', '')}
      />
    </div>
  );
};

export default PaymentOptions;
